// material/model — the generation side view's Model (phase 2c, design v2.1
// §5). One focused root material at a time; everything about it is an
// explicit state, never an ambiguous null:
//
//   Focus.None     — the side view shows nothing.
//   Focus.Shown    — the right panel focuses `root`: skeleton + live job feed
//                    + the author's in-flight intent + per-part disclosure.
//
// Pure data. Projections over it (rows, controls) live in `view-model.ts`.

import type { NewType } from "@bilda/utils";
import type {
    GenerationJobSnapshot,
    GenerationSkeleton,
    MaterialProvenance,
} from "../../app/ports.js";
import type { Element } from "../../components/Editor/types.js";
import type { MaterialRef } from "../../components/Navigation/types.js";

export type Remote<T> =
    | NewType<"Remote.Loading">
    | NewType<"Remote.Loaded", { readonly value: T }>
    | NewType<"Remote.Failed">;

export const loading: Remote<never> = { _tag: "Remote.Loading" };
export const failed: Remote<never> = { _tag: "Remote.Failed" };
export const loaded = <T>(value: T): Remote<T> => ({
    _tag: "Remote.Loaded",
    value,
});

// The job as the live feed knows it. `Lost` keeps the last snapshot it saw
// (`null` = it knew of no job) so the rows stay put while it reconnects.
export type JobFeed =
    | NewType<"JobFeed.Loading">
    | NewType<"JobFeed.NoJob">
    | NewType<"JobFeed.Live", { readonly snapshot: GenerationJobSnapshot }>
    | NewType<"JobFeed.Lost", { readonly last: GenerationJobSnapshot | null }>;

// The author's job intent awaiting its answer (optimistic: the control shows
// it at once). `idle` = nothing in flight.
type PendingIntent = "idle" | "starting" | "stopping" | "continuing";

// A job intent the server refused or never answered; shown in plain words
// next to the controls until the next intent.
export type JobIntent =
    | NewType<"Intent.Start">
    | NewType<"Intent.Stop">
    | NewType<"Intent.Continue">
    | NewType<"Intent.Revise", { readonly part: string }>
    | NewType<"Intent.StillFine", { readonly part: string }>
    | NewType<"Intent.UseVersion", { readonly part: string }>
    | NewType<"Intent.KeepVersion", { readonly part: string }>
    | ReviewIntent;

// The author's review of a part (phase 5a): approve its newest version, or
// take an approval back. Distinct intents.
export type ReviewIntent =
    | NewType<"Intent.Approve", { readonly part: string }>
    | NewType<"Intent.RevokeApproval", { readonly part: string }>;

// A review intent in flight (optimistic: the row shows its outcome at once).
// `sending` = awaiting the server; `sent` = accepted, awaiting the skeleton
// re-read that shows it, so the row never flickers back in between.
type Reviewing = {
    readonly intent: ReviewIntent["_tag"];
    readonly phase: "sending" | "sent";
};

// An answer to a part's notice: `sending` = awaiting the server; `sent` =
// accepted, awaiting the skeleton re-read that shows it settled (so the
// notice never flickers back in between).
export type Answer = "sending" | "sent";
// An outdated part's "Still fine" answer.
export type StillFine = Answer;
// `Ungrounded`: the material has no sources, and generation never writes
// ungrounded content (the server refuses the start).
export type Problem =
    | NewType<"Problem.None">
    | NewType<"Problem.IntentFailed", { readonly intent: JobIntent }>
    | NewType<"Problem.Ungrounded">;

export const noProblem: Problem = { _tag: "Problem.None" };

// A landed part's content, read on demand when the author opens it.
export type PartContent = {
    readonly elements: readonly Element[];
    readonly provenance: MaterialProvenance;
};

// Content is keyed by part AND version: a revision that lands a newer
// version is a different read. `head` = a part written before any job (its
// version is not on the skeleton).
export const contentKey = (part: string, version: number | "head"): string =>
    `${part}@${version}`;

export type Shown = NewType<
    "Focus.Shown",
    {
        readonly root: MaterialRef;
        readonly skeleton: Remote<GenerationSkeleton>;
        readonly feed: JobFeed;
        readonly pending: PendingIntent;
        // Parts whose revision request is in flight.
        readonly revising: ReadonlySet<string>;
        // Outdated parts whose "Still fine" is in flight, by part id.
        readonly stillFine: ReadonlyMap<string, StillFine>;
        // Parts whose newer-versions notice was answered (use a version, or
        // keep the pinned one), by part id.
        readonly pinAnswers: ReadonlyMap<string, Answer>;
        // Parts whose approve / revoke is in flight, by part id.
        readonly reviewing: ReadonlyMap<string, Reviewing>;
        readonly problem: Problem;
        // Parts the author opened (progressive disclosure).
        readonly expanded: ReadonlySet<string>;
        readonly contents: ReadonlyMap<string, Remote<PartContent>>;
    }
>;

export type Focus = NewType<"Focus.None"> | Shown;

export type MaterialModel = { readonly focus: Focus };

export const initialMaterialModel: MaterialModel = {
    focus: { _tag: "Focus.None" },
};

export const mkShown = (
    root: MaterialRef,
    skeleton: Remote<GenerationSkeleton>,
    feed: JobFeed,
): Shown => ({
    _tag: "Focus.Shown",
    root,
    skeleton,
    feed,
    pending: "idle",
    revising: new Set(),
    stillFine: new Map(),
    pinAnswers: new Map(),
    reviewing: new Map(),
    problem: noProblem,
    expanded: new Set(),
    contents: new Map(),
});
